import React, { useState, useEffect } from 'react';
import { collection, addDoc, serverTimestamp, orderBy, query, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import EditableContentMultilang from '../components/EditableContentMultilang';
import ImageUploader from '../components/ImageUploader';
import { useAuth } from '../AuthContext';
import './GalleryPage.css';

const GalleryImage = ({ src, alt, docId, caption, onDelete }) => {
    const { currentUser } = useAuth();
    const [isLoaded, setIsLoaded] = useState(false);
    const fallbackSrc = '/placeholder-image.jpg';

    return (
        <div className="gallery-item">
            <img
                src={src || fallbackSrc}
                alt={alt || 'Immagine galleria'}
                onLoad={() => setIsLoaded(true)}
                onError={(e) => {
                    if (e.target.src !== fallbackSrc) { e.target.onerror = null; e.target.src = fallbackSrc; }
                    setIsLoaded(true);
                }}
                className={isLoaded ? 'loaded' : ''}
                style={{ opacity: isLoaded ? 1 : 0 }}
            />
            <div className="caption-container">
                 <EditableContentMultilang docPath={`gallery_images/${docId}`} field="caption" elementType="p" placeholder="Aggiungi una didascalia..."/>
            </div>
            {currentUser && (
                <button onClick={() => onDelete(docId, caption)} className="delete-image-button" title="Elimina Immagine">🗑️</button>
            )}
        </div>
    );
};

const GalleryPage = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const { currentUser } = useAuth();
    const [showAddForm, setShowAddForm] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [addError, setAddError] = useState('');
    const [addSuccess, setAddSuccess] = useState('');

    useEffect(() => {
        setLoading(true);
        const imagesQuery = query(collection(db, 'gallery_images'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(imagesQuery, (querySnapshot) => {
            setImages(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        }, (error) => {
            console.error("Errore nel caricamento galleria:", error);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleUploadComplete = async (imageUrl) => {
        if (!imageUrl) {
            setAddError("Caricamento fallito o annullato.");
            return;
        }
        setIsAdding(true);
        setAddError('');
        setAddSuccess('');
        try {
            await addDoc(collection(db, 'gallery_images'), {
                imageUrl: imageUrl,
                caption: "", // La didascalia può essere aggiunta dopo
                createdAt: serverTimestamp(),
                addedBy: currentUser?.email || 'unknown'
            });
            setAddSuccess('Immagine aggiunta con successo!');
            setShowAddForm(false);
            setTimeout(() => setAddSuccess(''), 4000);
        } catch (error) {
            console.error("Errore nel salvataggio immagine in galleria:", error);
            setAddError("Errore durante il salvataggio dell'immagine. Riprova.");
        } finally {
            setIsAdding(false);
        }
    };
    
    const handleDeleteImage = async (docId, caption) => {
        const imageName = caption || `l'immagine`;
        if(window.confirm(`Sei sicuro di voler eliminare "${imageName}"?`)) {
            try {
                await deleteDoc(doc(db, 'gallery_images', docId));
                alert('Immagine rimossa dalla galleria.');
            } catch (error) {
                console.error("Errore eliminazione immagine:", error);
                alert("Errore durante l'eliminazione dell'immagine.");
            }
        }
    };

    return (
                    <div className="gallery-page">
                <section className="page-header">
                    <div className="container text-center">
                        <EditableContentMultilang docPath="gallery_page/header" field="title" elementType="h1" />
                        <EditableContentMultilang docPath="gallery_page/header" field="description" elementType="p" />
                         {currentUser && (
                             <button onClick={() => setShowAddForm(prev => !prev)} className="cta-button add-image-toggle-button">
                                 {showAddForm ? 'Annulla' : '+ Aggiungi Immagine'}
                             </button>
                         )}
                    </div>
                </section>

                {showAddForm && currentUser && (
                    <section className="add-image-form-section">
                        <div className="container">
                            <div className="add-image-form">
                                <h3>Carica una nuova immagine</h3>
                                <ImageUploader
                                    currentImageUrl=""
                                    onUploadComplete={handleUploadComplete}
                                    folderPath="gallery"
                                />
                                {addError && <p className="form-status error">{addError}</p>}
                                {addSuccess && <p className="form-status success">{addSuccess}</p>}
                            </div>
                        </div>
                    </section>
                )}

                <section className="content-section gallery-display-section">
                    <div className="container">
                        {loading && <p className="loading-text">Caricamento galleria...</p>}
                        {!loading && images.length === 0 && (
                            <p className="no-images-text">Nessuna immagine trovata nella galleria.</p>
                        )}
                        {images.length > 0 && (
                            <div className="gallery-grid">
                                {images.map(image => (
                                    <GalleryImage
                                        key={image.id}
                                        docId={image.id}
                                        src={image.imageUrl}
                                        alt={image.caption || `Immagine ${image.id}`}
                                        caption={image.caption}
                                        onDelete={handleDeleteImage}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            </div>
    );
};

export default GalleryPage;

